import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { defer, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

interface HCaptcha {
  render(container: HTMLElement, params: { sitekey: string; size: 'invisible' }): string;
  execute(widgetId: string, options: { async: true }): Promise<{ response: string; key: string }>;
  remove(widgetId: string): void;
}

const ONLOAD_CALLBACK = 'appicHCaptchaOnLoad';
const SCRIPT_URL = `https://js.hcaptcha.com/1/api.js?render=explicit&recaptchacompat=off&onload=${ONLOAD_CALLBACK}`;

@Injectable({ providedIn: 'root' })
export class CaptchaService {
  private api: Promise<HCaptcha> | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  /** Runs an invisible hCaptcha challenge and emits the response token to send as `h_captcha_response`. */
  execute(): Observable<string> {
    if (!isPlatformBrowser(this.platformId)) {
      return throwError(() => new Error('hCaptcha is only available in the browser'));
    }
    return defer(() => this.runChallenge());
  }

  private loadApi(): Promise<HCaptcha> {
    if (!this.api) {
      this.api = new Promise((resolve, reject) => {
        const win = this.document.defaultView as Window & { hcaptcha?: HCaptcha };
        win[ONLOAD_CALLBACK] = () => resolve(win.hcaptcha);

        const script = this.document.createElement('script');
        script.src = SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
          this.api = null; /* allow retrying on the next execute */
          script.remove();
          reject(new Error('Failed to load hCaptcha'));
        };
        this.document.head.appendChild(script);
      });
    }
    return this.api;
  }

  /* A fresh widget per call, so concurrent executions (e.g. email validation + submit) don't clash */
  private async runChallenge(): Promise<string> {
    const hcaptcha = await this.loadApi();
    const container = this.document.createElement('div');
    this.document.body.appendChild(container);
    const widgetId = hcaptcha.render(container, { sitekey: environment.hcaptcha, size: 'invisible' });

    try {
      const { response } = await hcaptcha.execute(widgetId, { async: true });
      return response;
    } catch (code) {
      throw new Error(`hCaptcha: ${code}`);
    } finally {
      hcaptcha.remove(widgetId);
      container.remove();
    }
  }
}
